import type { API } from "../types";

export type PortfolioStatus = "duplicate" | "review" | "canonical";

export interface PortfolioApiRow {
  api: API;
  name: string;
  path: string;
  domain: string;
  consumers: number;
  callsPerDay: number;
  duplicateScore: number;
  status: PortfolioStatus;
}

export interface PortfolioSummary {
  totalApis: number;
  domainCount: number;
  ungovernedPercent: number;
  duplicateRiskCount: number;
  reuseRatePercent: number;
  reuseTargetPercent: number;
  scannedAt: Date;
}

const REUSE_TARGET_PERCENT = 70;

function hashSeed(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function seededValue(uuid: string, salt: string, min: number, max: number): number {
  const hash = hashSeed(`${uuid}:${salt}`);
  return min + (hash % (max - min + 1));
}

export function deriveApiDomain(api: API): string {
  const classification = api.classifications?.trim();
  if (classification) return classification;

  const entityClass = api.entityClass?.trim();
  if (entityClass) return entityClass;

  const firstTag = api.tags?.find((tag) => tag.trim().length > 0);
  if (firstTag) return firstTag;

  return "Uncategorized";
}

function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

export function extractApiPath(api: API): string {
  const raw = api.specContent?.trim();
  if (!raw) {
    return `/v1/${slugifyName(api.name) || "api"}`;
  }

  try {
    const spec = JSON.parse(raw) as {
      paths?: Record<string, unknown>;
      servers?: Array<{ url?: string }>;
    };

    const pathKeys = spec.paths ? Object.keys(spec.paths) : [];
    if (pathKeys.length > 0) {
      return pathKeys.sort()[0];
    }

    const serverUrl = spec.servers?.[0]?.url?.trim();
    if (serverUrl) {
      try {
        const url = new URL(serverUrl);
        return url.pathname !== "/" ? url.pathname : `/v1/${slugifyName(api.name) || "api"}`;
      } catch {
        if (serverUrl.startsWith("/")) return serverUrl;
      }
    }
  } catch {
    // YAML or invalid JSON — fall through to slug path
  }

  return `/v1/${slugifyName(api.name) || "api"}`;
}

export function deriveMockDuplicateScore(api: API): number {
  return seededValue(api.uuid, "duplicate-score", 5, 98);
}

export function deriveStatusFromScore(score: number): PortfolioStatus {
  if (score >= 70) return "duplicate";
  if (score >= 40) return "review";
  return "canonical";
}

export function deriveMockConsumers(api: API): number {
  return seededValue(api.uuid, "consumers", 1, 45);
}

export function deriveMockCallsPerDay(api: API): number {
  return seededValue(api.uuid, "calls-per-day", 500, 250_000);
}

export function formatCallsPerDay(calls: number): string {
  if (calls >= 1_000_000) return `${(calls / 1_000_000).toFixed(1)}M`;
  if (calls >= 1_000) return `${Math.round(calls / 1_000)}K`;
  return String(calls);
}

export function formatRelativeScanTime(scannedAt: Date, now = new Date()): string {
  const diffMs = Math.max(0, now.getTime() - scannedAt.getTime());
  const diffMinutes = Math.floor(diffMs / 60_000);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes === 1) return "1 minute ago";
  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours === 1) return "1 hour ago";
  return `${diffHours} hours ago`;
}

function deriveMockReuseRatePercent(apis: API[]): number {
  if (apis.length === 0) return 0;

  const scores = apis.map((api) => deriveMockDuplicateScore(api));
  const avgDuplicate = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  const reuse = Math.max(0, Math.min(100, Math.round(100 - avgDuplicate * 0.66)));
  return reuse;
}

export function buildPortfolioApiRow(api: API): PortfolioApiRow {
  const duplicateScore = deriveMockDuplicateScore(api);

  return {
    api,
    name: api.name,
    path: extractApiPath(api),
    domain: deriveApiDomain(api),
    consumers: deriveMockConsumers(api),
    callsPerDay: deriveMockCallsPerDay(api),
    duplicateScore,
    status: deriveStatusFromScore(duplicateScore),
  };
}

export function computePortfolioSummary(apis: API[], scannedAt = new Date()): PortfolioSummary {
  const rows = apis.map(buildPortfolioApiRow);
  const domains = new Set(rows.map((row) => row.domain));
  const ungovernedCount = apis.filter((api) => !api.architectReview?.trim()).length;
  const duplicateRiskCount = rows.filter((row) => row.duplicateScore >= 70).length;

  return {
    totalApis: apis.length,
    domainCount: domains.size,
    ungovernedPercent: apis.length === 0 ? 0 : Math.round((ungovernedCount / apis.length) * 100),
    duplicateRiskCount,
    reuseRatePercent: deriveMockReuseRatePercent(apis),
    reuseTargetPercent: REUSE_TARGET_PERCENT,
    scannedAt,
  };
}

export function sortPortfolioRows(
  rows: PortfolioApiRow[],
  sortKey: "duplicateScore" | "name" | "callsPerDay" | "consumers",
  sortDir: "asc" | "desc",
): PortfolioApiRow[] {
  const sorted = [...rows].sort((a, b) => {
    let av: string | number = "";
    let bv: string | number = "";

    switch (sortKey) {
      case "name":
        av = a.name.toLowerCase();
        bv = b.name.toLowerCase();
        break;
      case "callsPerDay":
        av = a.callsPerDay;
        bv = b.callsPerDay;
        break;
      case "consumers":
        av = a.consumers;
        bv = b.consumers;
        break;
      default:
        av = a.duplicateScore;
        bv = b.duplicateScore;
    }

    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return sorted;
}

export function getStatusLabel(status: PortfolioStatus): string {
  switch (status) {
    case "duplicate":
      return "Duplicate found";
    case "review":
      return "Review suggested";
    default:
      return "Canonical";
  }
}
